import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Wallet, TrendingUp, Clock3, Printer, HandCoins, Loader2, PlusCircle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import AppLayout from "../components/AppLayout";
import { canAccess } from "../utils/auth";
import SelectField from "../components/ui/SelectField";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_API_URL;

const money = (v) =>
  Number(v || 0).toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const KpiCard = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-4 flex items-center gap-3 flex-1 min-w-[200px]">
    <div className={`p-2.5 rounded-xl ${tone}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-800 truncate">${money(value)}</p>
    </div>
  </div>
);

const IngresosEgresosChart = ({ serie }) => {
  if (!serie?.length) {
    return <p className="text-sm text-gray-400 italic py-8 text-center">Sin movimientos en el rango seleccionado</p>;
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={serie} barGap={2}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" vertical={false} />
        <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: "#898781" }} tickFormatter={(v) => v.slice(5)} />
        <YAxis tick={{ fontSize: 11, fill: "#898781" }} width={70} tickFormatter={(v) => `$${money(v)}`} />
        <Tooltip formatter={(v) => `$${money(v)}`} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="ingresos" name="Ingresos" fill="#0ca30c" radius={[4, 4, 0, 0]} />
        <Bar dataKey="egresos" name="Egresos" fill="#d03b3b" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const METODO_COLORS = { EFECTIVO: "#2a78d6", TRANSFERENCIA: "#1baf7a", TARJETA: "#eda100" };
const METODO_LABELS = { EFECTIVO: "Efectivo", TRANSFERENCIA: "Transferencia", TARJETA: "Tarjeta" };

const MetodosPagoChart = ({ metodosPago }) => {
  if (!metodosPago?.length) {
    return <p className="text-sm text-gray-400 italic py-8 text-center">Sin pagos registrados este mes</p>;
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={metodosPago}
          dataKey="total"
          nameKey="metodo_pago"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={2}
          label={({ metodo_pago, total }) => `${METODO_LABELS[metodo_pago] || metodo_pago}: $${money(total)}`}
        >
          {metodosPago.map((m) => (
            <Cell key={m.metodo_pago} fill={METODO_COLORS[m.metodo_pago] || "#898781"} />
          ))}
        </Pie>
        <Tooltip formatter={(v) => `$${money(v)}`} />
        <Legend
          formatter={(value) => METODO_LABELS[value] || value}
          wrapperStyle={{ fontSize: 12 }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

const TABS = [
  { key: "pendientes", label: "Pendientes / Abonos", perm: "/caja" },
  { key: "historico", label: "Histórico", perm: "/caja/historico" },
  { key: "movimientos", label: "Movimientos", perm: "/caja/movimientos" },
];

const CajaPage = () => {
  const token = localStorage.getItem("token");
  const [resumen, setResumen] = useState(null);
  const [tab, setTab] = useState("pendientes");
  const [facturas, setFacturas] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [showAbonoModal, setShowAbonoModal] = useState(false);
  const [abonoRow, setAbonoRow] = useState(null);
  const [abonoForm, setAbonoForm] = useState({ monto: "", metodo_pago: "EFECTIVO", descripcion: "" });
  const [saving, setSaving] = useState(false);

  const [movimientos, setMovimientos] = useState([]);
  const [loadingMov, setLoadingMov] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [showMovModal, setShowMovModal] = useState(false);
  const [movForm, setMovForm] = useState({ tipo: "INGRESO", categoria_cod: "", metodo_pago: "EFECTIVO", monto: "", descripcion: "" });
  const [savingMov, setSavingMov] = useState(false);

  const fetchMovimientos = async () => {
    setLoadingMov(true);
    try {
      const res = await axios.get(`${API_URL}/caja/movimientos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMovimientos(res.data?.items || []);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar movimientos");
    } finally {
      setLoadingMov(false);
    }
  };

  const fetchCategorias = async (tipo) => {
    try {
      const res = await axios.get(`${API_URL}/caja/categorias`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { tipo },
      });
      setCategorias(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (tab === "movimientos") fetchMovimientos();
  }, [tab]); // eslint-disable-line

  useEffect(() => {
    if (showMovModal) fetchCategorias(movForm.tipo);
  }, [showMovModal, movForm.tipo]); // eslint-disable-line

  const resetMovForm = () => {
    setMovForm({ tipo: "INGRESO", categoria_cod: "", metodo_pago: "EFECTIVO", monto: "", descripcion: "" });
  };

  const openMovModal = () => {
    resetMovForm();
    setShowMovModal(true);
  };

  const closeMovModal = () => {
    resetMovForm();
    setShowMovModal(false);
  };

  const submitMovimiento = async () => {
    const montoNum = Number(movForm.monto);
    if (!(montoNum > 0)) {
      toast.error("Ingrese un monto válido mayor a 0");
      return;
    }
    setSavingMov(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/caja/movimientos`,
        { ...movForm, monto: montoNum, usuario_cod: user.usuario },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Movimiento registrado correctamente");
      setShowMovModal(false);
      resetMovForm();
      fetchMovimientos();
      fetchResumen();
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudo registrar el movimiento");
    } finally {
      setSavingMov(false);
    }
  };

  const METODOS_PAGO_OPTIONS = [
    { value: "EFECTIVO", label: "Efectivo" },
    { value: "TRANSFERENCIA", label: "Transferencia" },
    { value: "TARJETA", label: "Tarjeta" },
  ];

  const visibleTabs = TABS.filter((t) => canAccess(t.perm));

  const fetchResumen = async () => {
    try {
      const res = await axios.get(`${API_URL}/caja/resumen`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { range: 7 },
      });
      setResumen(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar el resumen de caja");
    }
  };

  useEffect(() => { fetchResumen(); }, []); // eslint-disable-line

  const fetchFacturas = async (estado) => {
    setLoadingFacturas(true);
    try {
      const res = await axios.get(`${API_URL}/caja/facturas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { estado },
      });
      setFacturas(res.data?.items || []);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar facturas");
    } finally {
      setLoadingFacturas(false);
    }
  };

  useEffect(() => {
    if (tab === "pendientes") fetchFacturas("pendiente");
    if (tab === "historico") fetchFacturas("pagada");
  }, [tab]); // eslint-disable-line

  const openAbonoModal = (row) => {
    setAbonoRow(row);
    setAbonoForm({ monto: String(row.saldo.toFixed(2)), metodo_pago: "EFECTIVO", descripcion: "" });
    setShowAbonoModal(true);
  };

  const submitAbono = async () => {
    if (!abonoRow) return;
    const montoNum = Number(abonoForm.monto);
    if (!(montoNum > 0)) {
      toast.error("Ingrese un monto válido mayor a 0");
      return;
    }
    if (montoNum > abonoRow.saldo + 0.01) {
      toast.error(`El monto no puede superar el saldo pendiente ($${money(abonoRow.saldo)})`);
      return;
    }
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/caja/pagos`,
        {
          factura: abonoRow.factura,
          monto: Number(abonoForm.monto),
          metodo_pago: abonoForm.metodo_pago,
          descripcion: abonoForm.descripcion,
          usuario_cod: user.usuario,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Abono registrado correctamente");
      setShowAbonoModal(false);
      fetchFacturas("pendiente");
      fetchResumen();
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudo registrar el abono");
    } finally {
      setSaving(false);
    }
  };

  const printInvoice = (factura) => {
    window.open(`${API_URL}/invoices/${factura}/pdf`, "_blank", "noopener,noreferrer");
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 rounded-xl">
            <Wallet className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Caja</h1>
            <p className="text-sm text-gray-500">Saldo, abonos de facturas y movimientos de caja</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <KpiCard icon={Wallet} label="Saldo actual" value={resumen?.saldoActual} tone="bg-brand-100 text-brand-700" />
          <KpiCard icon={Clock3} label="Pendiente por cobrar" value={resumen?.pendienteCobrar} tone="bg-amber-100 text-amber-700" />
          <KpiCard icon={TrendingUp} label="Cobrado hoy" value={resumen?.cobradoHoy} tone="bg-emerald-100 text-emerald-700" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Ingresos vs Egresos (7 días)</h3>
            <IngresosEgresosChart serie={resumen?.serie} />
          </div>
          <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Métodos de pago (mes actual)</h3>
            <MetodosPagoChart metodosPago={resumen?.metodosPago} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-1.5 flex flex-wrap gap-1">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                tab === t.key ? "bg-brand-gradient text-white shadow-sm" : "text-gray-600 hover:bg-brand-50 hover:text-brand-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="animate-fadeIn">
          {(tab === "pendientes" || tab === "historico") && (
            <div className="overflow-x-auto rounded-xl border border-brand-100 bg-white">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-brand-gradient text-white">
                    {["Factura", "Fecha", "Cliente", "Propietario", "Total", "Abonado", "Saldo", "Estado", ""].map((h, i) => (
                      <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider ${i >= 4 && i <= 6 ? "text-right" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-50">
                  {loadingFacturas ? (
                    <tr><td colSpan="9" className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-brand-400 mx-auto" /></td></tr>
                  ) : facturas.length === 0 ? (
                    <tr><td colSpan="9" className="py-8 text-center text-gray-400 text-sm italic">No hay facturas para mostrar</td></tr>
                  ) : (
                    facturas.map((row, i) => (
                      <tr key={row.factura} className={`${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}>
                        <td className="px-4 py-3 font-mono text-xs bg-brand-100 text-brand-700 rounded">{row.factura}</td>
                        <td className="px-4 py-3 text-gray-600">{row.fecha_factura}</td>
                        <td className="px-4 py-3 text-gray-700">{row.cliente || "N/A"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.propietario || "N/A"}</td>
                        <td className="px-4 py-3 text-right">${money(row.total)}</td>
                        <td className="px-4 py-3 text-right text-emerald-700">${money(row.abonado)}</td>
                        <td className="px-4 py-3 text-right font-semibold">${money(row.saldo)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            row.estado === "PAGADA" ? "bg-emerald-100 text-emerald-700"
                            : row.estado === "PARCIAL" ? "bg-amber-100 text-amber-700"
                            : "bg-gray-100 text-gray-600"
                          }`}>{row.estado}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <button onClick={() => printInvoice(row.factura)} className="flex items-center gap-1.5 text-brand-600 hover:text-brand-800 font-medium text-xs">
                              <Printer className="h-3.5 w-3.5" /> PDF
                            </button>
                            {tab === "pendientes" && canAccess("/caja/pagos") && (
                              <button onClick={() => openAbonoModal(row)} className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-800 font-medium text-xs">
                                <HandCoins className="h-3.5 w-3.5" /> Abonar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "movimientos" && (
            <div className="space-y-3">
              {canAccess("/caja/movimientos") && (
                <div className="flex justify-end">
                  <Button onClick={openMovModal} className="gap-2">
                    <PlusCircle className="h-4 w-4" /> Nuevo movimiento
                  </Button>
                </div>
              )}
              <div className="overflow-x-auto rounded-xl border border-brand-100 bg-white">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-brand-gradient text-white">
                      {["Fecha/Hora", "Tipo", "Origen", "Monto", "Método", "Detalle", "Usuario"].map((h, i) => (
                        <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider ${i === 3 ? "text-right" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-50">
                    {loadingMov ? (
                      <tr><td colSpan="7" className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-brand-400 mx-auto" /></td></tr>
                    ) : movimientos.length === 0 ? (
                      <tr><td colSpan="7" className="py-8 text-center text-gray-400 text-sm italic">No hay movimientos</td></tr>
                    ) : (
                      movimientos.map((m, i) => (
                        <tr key={m.mov_id} className={`${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}>
                          <td className="px-4 py-3 text-gray-600 text-xs">{new Date(m.fecha_hora).toLocaleString("es-CO")}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${m.tipo === "INGRESO" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                              {m.tipo}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs">{m.origen === "FACTURA" ? `Factura ${m.factura}` : "Manual"}</td>
                          <td className="px-4 py-3 text-right font-semibold">${money(m.monto)}</td>
                          <td className="px-4 py-3 text-gray-600 text-xs">{m.metodo_pago}</td>
                          <td className="px-4 py-3 text-gray-600 text-xs">{m.categoria_detalle || m.descripcion || "—"}</td>
                          <td className="px-4 py-3 text-gray-600 text-xs">{m.usuario_cod}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {showAbonoModal && abonoRow && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 space-y-4">
                <h3 className="text-lg font-bold text-gray-800">Abonar factura {abonoRow.factura}</h3>
                <p className="text-sm text-gray-500">Saldo pendiente: <strong>${money(abonoRow.saldo)}</strong></p>
                <div>
                  <label className="block text-sm font-medium mb-1">Monto *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={abonoRow.saldo}
                    value={abonoForm.monto}
                    onChange={(e) => setAbonoForm({ ...abonoForm, monto: e.target.value })}
                    className="w-full border rounded-lg p-2"
                  />
                </div>
                <SelectField
                  label="Método de pago"
                  name="metodo_pago"
                  value={abonoForm.metodo_pago}
                  onChange={(e) => setAbonoForm({ ...abonoForm, metodo_pago: e.target.value })}
                  options={METODOS_PAGO_OPTIONS}
                  valueKey="value"
                  displayFn={(o) => o.label}
                />
                <div>
                  <label className="block text-sm font-medium mb-1">Descripción (opcional)</label>
                  <input
                    type="text"
                    value={abonoForm.descripcion}
                    onChange={(e) => setAbonoForm({ ...abonoForm, descripcion: e.target.value })}
                    className="w-full border rounded-lg p-2"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" type="button" onClick={() => setShowAbonoModal(false)}>Cancelar</Button>
                  <Button onClick={submitAbono} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar abono"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showMovModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 space-y-4">
                <h3 className="text-lg font-bold text-gray-800">Nuevo movimiento</h3>
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo *</label>
                  <select
                    value={movForm.tipo}
                    onChange={(e) => setMovForm({ ...movForm, tipo: e.target.value, categoria_cod: "" })}
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="INGRESO">Ingreso</option>
                    <option value="EGRESO">Egreso</option>
                  </select>
                </div>
                <SelectField
                  label="Categoría"
                  name="categoria_cod"
                  value={movForm.categoria_cod}
                  onChange={(e) => setMovForm({ ...movForm, categoria_cod: e.target.value })}
                  options={categorias}
                  valueKey="codigo"
                  displayFn={(o) => o.detalle}
                  required
                />
                <div>
                  <label className="block text-sm font-medium mb-1">Monto *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={movForm.monto}
                    onChange={(e) => setMovForm({ ...movForm, monto: e.target.value })}
                    className="w-full border rounded-lg p-2"
                  />
                </div>
                <SelectField
                  label="Método de pago"
                  name="metodo_pago"
                  value={movForm.metodo_pago}
                  onChange={(e) => setMovForm({ ...movForm, metodo_pago: e.target.value })}
                  options={METODOS_PAGO_OPTIONS}
                  valueKey="value"
                  displayFn={(o) => o.label}
                />
                <div>
                  <label className="block text-sm font-medium mb-1">Descripción</label>
                  <input
                    type="text"
                    value={movForm.descripcion}
                    onChange={(e) => setMovForm({ ...movForm, descripcion: e.target.value })}
                    className="w-full border rounded-lg p-2"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" type="button" onClick={closeMovModal}>Cancelar</Button>
                  <Button onClick={submitMovimiento} disabled={savingMov || !movForm.categoria_cod}>
                    {savingMov ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default CajaPage;
